import { useState, useEffect, useMemo } from "react";
import React from 'react';
import Box from '@mui/material/Box';
import {
  useGridApiRef,
} from '@mui/x-data-grid-premium';
import EditableDataGrid from "components/EditableDataGrid/EditableDataGrid";
import Typography from '@mui/material/Typography';
import { NCMFormatter } from 'utils';
import Chip from '@mui/material/Chip';
import Badge from '@mui/material/Badge';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import Checkbox from '@mui/material/Checkbox';
import { PURPOSE_CHOICES } from 'constants';
import { useOrderType } from 'views/admin/pm-main/comex/detail/context/OrderTypeContext';

const ProductTax = ({ formData }) => {
  const resourcePath = "tax/product-tax";
  const relatedResources = `tax/product-tax/?productitemsalesorder__sales_order=${formData.id}`;
  const [tableData, setTableData] = useState([]);
  const [productTaxItems, setProductTaxItems] = useState([]);
  const { isEncomenda, isDistribution } = useOrderType();

  const apiRef = useGridApiRef();

  const actions = {
    create: false,
    edit: true,
    delete: false,
  }

  useEffect(() => {
    setProductTaxItems(tableData);
  }, [tableData]);


  const baseColumns = [
    {
      field: 'product',
      headerName: 'PRODUCT',
      flex: 2.5,
      minWidth: 350,
      editable: false,
      renderCell: (params) => {
        const { product, quantity } = params.row;
        if (!product) return '';

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', height: '100%', py: 0.5 }}>
            {/* Avatar/Badge Section */}
            <Box sx={{ mr: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <Tooltip title={`Quantity: ${quantity}`} placement="top">
                {product.picture ? (
                  <Badge
                    badgeContent={
                      <Chip
                        size="small"
                        label={quantity}
                        color="primary"
                        sx={{
                          height: 18,
                          minWidth: 24,
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    }
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <Avatar
                      src={product.picture}
                      alt={product.name}
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        boxShadow: '0px 1px 3px rgba(0,0,0,0.1)',
                        border: '1px solid #eee'
                      }}
                    />
                  </Badge>
                ) : (
                  <Badge
                    badgeContent={
                      <Chip
                        size="small"
                        label={quantity}
                        color="primary"
                        sx={{
                          height: 18,
                          minWidth: 24,
                          fontSize: '0.65rem',
                          fontWeight: 'bold',
                          '& .MuiChip-label': {
                            px: 0.5
                          }
                        }}
                      />
                    }
                    overlap="circular"
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'right',
                    }}
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: 'grey.100',
                        boxShadow: '0px 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      {product.name ? product.name.charAt(0) : ' '}
                    </Avatar>
                  </Badge>
                )}
              </Tooltip>
            </Box>
            {/* Text Details Section - Vertically Centered */}
            <Box sx={{ overflow: 'hidden', flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <Tooltip title={product.name} placement="top-start">
                <Typography
                  variant="subtitle2"
                  noWrap
                  sx={{
                    fontWeight: 500,
                    lineHeight: 1.3,
                    mb: 0.75 // Margin below name
                  }}
                >
                  {product.name || '-'}
                </Typography>
              </Tooltip>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <Chip
                  label={product.ncm_code ? NCMFormatter(product.ncm_code) : 'N/A'}
                  size="small"
                  variant="outlined"
                  sx={{
                    height: 18,
                    fontSize: '0.65rem',
                    borderColor: 'grey.300',
                    color: 'text.secondary',
                    '& .MuiChip-label': {
                      px: 0.6
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
        );
      },
    },
    {
      field: 'product_purpose',
      headerName: 'PURPOSE',
      flex: 1,
      minWidth: 180,
      editable: true,
      type: 'singleSelect',
      valueOptions: PURPOSE_CHOICES.map(choice => ({
        value: choice.value,
        label: choice.label
      })),
      renderCell: (params) => {
        const purposeChoice = PURPOSE_CHOICES.find(choice => choice.value === params.value);
        if (!purposeChoice) return '-';

        return (
          <Chip
            label={purposeChoice.label}
            size="small"
            variant="outlined"
            sx={{
              borderColor: 'primary.main',
              color: 'primary.main',
              fontWeight: 500
            }}
          />
        );
      },
    },
    {
      field: 'aliq_interna_icms_st',
      headerName: 'ALIQ INTERNA ICMS', // utilizado para configuração de aliquota interna do importador visto que essa pode ser um pouco diferente da padrão
      flex: 1,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        return value ? `${(parseFloat(value)).toFixed(2)}%` : '0%';
      },
      valueGetter: (value) => {
        // Convert from decimal to percentage for display and editing
        return value ? parseFloat(value) * 100 : 0;
      },
      valueSetter: (value, row) => {
        // Convert from percentage to decimal for storage
        const decimalValue = parseFloat(value) / 100;
        return { ...row, aliq_interna_icms_st: decimalValue };
      },
    },
    {
      field: 'icms',
      headerName: 'ICMS',
      flex: 1,
      minWidth: 100,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        return value ? `${(parseFloat(value)).toFixed(2)}%` : '0%';
      },
      valueGetter: (value) => {
        // Convert from decimal to percentage for display and editing
        return value ? parseFloat(value) * 100 : 0;
      },
      valueSetter: (value, row) => {
        // Convert from percentage to decimal for storage
        const decimalValue = parseFloat(value) / 100;
        return { ...row, icms: decimalValue };
      },
    },
    {
      field: 'mva_ajustada',
      headerName: 'MVA AJUSTADA',
      flex: 1,
      minWidth: 130,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        return value ? `${(parseFloat(value)).toFixed(2)}%` : '0%';
      },
      valueGetter: (value) => {
        // Convert from decimal to percentage for display and editing
        return value ? parseFloat(value) * 100 : 0;
      },
      valueSetter: (value, row) => {
        // Convert from percentage to decimal for storage
        const decimalValue = parseFloat(value) / 100;
        return { ...row, mva_ajustada: decimalValue };
      },
    },
    {
      field: 'reducao_base_pis_cofins',
      headerName: 'REDUÇÃO PIS/COFINS',
      flex: 1,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        if (!value) return '0%';

        // valueGetter already converts decimal to percentage
        return `${parseFloat(value).toFixed(2)}%`;
      },
      valueGetter: (value) => {
        if (!value) return 0;

        // Convert from decimal to percentage for display and editing
        return parseFloat((parseFloat(value) * 100).toFixed(4));
      },
      valueSetter: (value, row) => {
        // Convert from percentage to decimal for storage
        const decimalValue = parseFloat((parseFloat(value) / 100).toFixed(6));
        return { ...row, reducao_base_pis_cofins: decimalValue };
      },
    },
    {
      field: 'majoracao_cofins',
      headerName: 'MAJORAÇÃO COFINS',
      flex: 1,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        if (!value) return '0%';

        // valueGetter already converts decimal to percentage
        return `${parseFloat(value).toFixed(2)}%`;
      },
      valueGetter: (value) => {
        if (!value) return 0;

        // Convert from decimal to percentage for display and editing
        return parseFloat((parseFloat(value) * 100).toFixed(4));
      },
      valueSetter: (value, row) => {
        // Convert from percentage to decimal for storage
        const decimalValue = parseFloat((parseFloat(value) / 100).toFixed(6));
        return { ...row, majoracao_cofins: decimalValue };
      },
    },
  ];

  // Sale-rate columns: monofásico flag + saída PIS/COFINS inputs. Shared by the
  // order types whose calc reads a per-product sale rate (Encomenda, Distribution).
  const saleRateColumns = [
    {
      field: 'pis_cofins_monofasico',
      headerName: 'MONOF\u00C1SICO',
      flex: 0.8,
      minWidth: 120,
      editable: true,
      type: 'boolean',
      renderCell: (params) => (
        <Checkbox
          checked={!!params.value}
          disabled
          size="small"
        />
      ),
    },
    {
      field: 'pct_pis_saida',
      headerName: 'PIS SA\u00CDDA (%)',
      flex: 1,
      minWidth: 130,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        return value ? `${parseFloat(value).toFixed(2)}%` : '0%';
      },
      valueGetter: (value) => {
        return value ? parseFloat(value) * 100 : 0;
      },
      valueSetter: (value, row) => {
        const decimalValue = parseFloat(value) / 100;
        return { ...row, pct_pis_saida: decimalValue };
      },
    },
    {
      field: 'pct_cofins_saida',
      headerName: 'COFINS SA\u00CDDA (%)',
      flex: 1,
      minWidth: 150,
      editable: true,
      type: 'number',
      renderCell: (params) => {
        const value = params.value;
        return value ? `${parseFloat(value).toFixed(2)}%` : '0%';
      },
      valueGetter: (value) => {
        return value ? parseFloat(value) * 100 : 0;
      },
      valueSetter: (value, row) => {
        const decimalValue = parseFloat(value) / 100;
        return { ...row, pct_cofins_saida: decimalValue };
      },
    },
  ];

  const columnsDataItems = useMemo(() => {
    if (isEncomenda || isDistribution) {
      return [...baseColumns, ...saleRateColumns];
    }
    return baseColumns;
  }, [isEncomenda, isDistribution]);


  return (
    <div className="h-full w-full rounded-[20px] px-3 pt-7 md:px-8">
      {/* Header */}
      <div className='flex justify-between items-center'>
        <h3 className="pt-[5px] text-2xl font-bold text-navy-700 dark:text-white">Product Tax Information</h3>
      </div>

      <Box>
        <EditableDataGrid
          columns={columnsDataItems}
          resourcePath={resourcePath}
          tableData={productTaxItems}
          setTableData={setTableData}
          relatedResources={relatedResources}
          apiRef={apiRef}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            pinnedColumns: { left: ['actions', 'picture'] },
            columns: {
              columnVisibilityModel: {}
            },
            filter: {
              filterModel: {
                items: [],
                quickFilterExcludeHiddenColumns: false,
              },
            },
          }}
          ignoreDiacritics
          actions={actions}
          autosizeOnMount={true}
          rowHeight={75}
          sx={{
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
              fontSize: '0.8rem'
            },
            '& .MuiDataGrid-row': {
              width: '100%'
            }
          }}
        />
      </Box>
    </div>
  );
}

export default ProductTax;